import React, { useState, useEffect } from "react";
import { 
  Row, 
  Col, 
  Card, 
  Tag, 
  Button, 
  Space, 
  Typography, 
  Spin,
  Descriptions,
  Avatar,
  Tabs,
  Steps,
  Alert,
} from "antd";
import { 
  ArrowLeftOutlined,
  ApartmentOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
  CreditCardOutlined
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { organizationService } from "../Services/OrganizationalService";
import { toast } from "react-toastify";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Step } = Steps;

const OrganizationDetailPage = () => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [approvingOrgId, setApprovingOrgId] = useState(null);

  useEffect(() => {
    if (organizationId) {
      fetchOrganizationDetails(organizationId);
    }
  }, [organizationId]);

  const fetchOrganizationDetails = async (id) => {
    try {
      setLoading(true);
      const data = await organizationService.getOrganizationDetails(id);
      setOrganization(data);
    } catch (error) {
      console.error('Error fetching organization details:', error);
      toast.error(error?.response?.data ||'Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleApproveOrganization = async (orgId) => {
    try {
      setApprovingOrgId(orgId);
      
      // Show loading toast
      toast.info("Checking available databases...");
      
      await organizationService.approveOrganization(orgId);
      
      // Show success toast
      toast.success("Organization approved successfully!");
      
      // Refresh the organization details
      await fetchOrganizationDetails(orgId);
      
    } catch (error) {
      console.error(" Error approving organization:", error);
      
      // Show error toast with specific message
      if (error.message === "No available databases found") {
        toast.error(error?.response?.data ||"No available databases found. Cannot approve organization.");
      } else {
        toast.error(error?.response?.data ||"Failed to approve organization. Please try again.");
      }
    } finally {
      setApprovingOrgId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Title level={3}>Organization not found</Title>
        <Button type="primary" onClick={handleBack}>
          Go Back
        </Button>
      </div>
    );
  }

  // Format date for display
  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleString() : 'N/A';
  };

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card className="card-professional">
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <Button 
                    type="text" 
                    icon={<ArrowLeftOutlined />} 
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  {organization.logoUrl ? (
                    <Avatar size="large" src={organization.logoUrl} />
                  ) : (
                    <Avatar size="large" icon={<ApartmentOutlined />} />
                  )}
                  <div>
                    <Title level={2} style={{ margin: 0 }}>
                      {organization.name}
                    </Title>
                    <Text type="secondary">
                      {organization.specialtyArea?.name || 'General Organization'}
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Tag color={organization.status === 0 ? 'orange' : 'green'} icon={organization.status === 0 ? <ClockCircleOutlined /> : <CheckCircleOutlined />}>
                    {organization.status === 0 ? 'PENDING' : 'APPROVED'}
                  </Tag>
                  <Tag color={organization.isActive ? 'green' : 'red'}>
                    {organization.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </Tag>
                </Space>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={18}>
          <Card className="card-professional">
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <TabPane tab="Overview" key="overview">
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  {/* Organization Information */}
                  <Card size="small" title="Organization Information" style={{ background: '#fafafa' }}>
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="Organization Name" span={2}>
                        <Text strong>{organization.name}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Organization ID">
                        {organization.organizationId || 'N/A'}
                      </Descriptions.Item>
                                          <Descriptions.Item label="Specialty Area">
                        <Tag color="blue">
                          {organization.specialtyArea?.name || `Area ${organization.specialtyAreaId}`}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Specialty Area Description">
                        {organization.specialtyArea?.description || 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Status">
                        <Tag color={organization.status === 0 ? 'orange' : 'green'}>
                          {organization.status === 0 ? 'PENDING' : 'APPROVED'}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Active">
                        <Tag color={organization.isActive ? 'green' : 'red'}>
                          {organization.isActive ? 'Yes' : 'No'}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>

                  {/* Address Information */}
                  <Card size="small" title="Address Information" style={{ background: '#fafafa' }}>
                    <Descriptions column={1} bordered size="small">
                      <Descriptions.Item label="Physical Address">
                        {organization.physicalAdress || 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="City">
                        {organization.city || 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="State">
                        {organization.state || 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Country">
                        {organization.country || 'N/A'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>


                  {/* Email Configuration */}
                  <Card size="small" title="Email Configuration" style={{ background: '#fafafa' }}>
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="SMTP Host">
                        {organization.smtpHost || 'Not configured'}
                      </Descriptions.Item>
                      <Descriptions.Item label="SMTP Port">
                        {organization.smtpPort || 'Not configured'}
                      </Descriptions.Item>
                      <Descriptions.Item label="SMTP From Email">
                        {organization.smtpFromEmail || 'Not configured'}
                      </Descriptions.Item>
                      <Descriptions.Item label="SMTP Password">
                        {organization.smtpPassword ? '••••••••' : 'Not configured'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>

                  {/* Important Dates */}
                  <Card size="small" title="Important Dates" style={{ background: '#fafafa' }}>
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="Created On">
                        {formatDate(organization.createdOn)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Last Modified">
                        {formatDate(organization.lastModifiedOn)}
                      </Descriptions.Item>
                      
                    </Descriptions>
                  </Card>
                </Space>
              </TabPane>
              
              <TabPane tab="Contacts" key="contacts">
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                  {/* Steps to show contact information */}
                  <Steps current={0} direction="vertical" size="small">
                    <Step 
                      title="Tenant Administrator" 
                      icon={<UserOutlined />} 
                      description="Primary admin account for the organization"
                    />
                    <Step 
                      title="Contract Administrator" 
                      icon={<ContactsOutlined />} 
                      description="Handles contracts and agreements"
                    />
                    <Step 
                      title="Billing Contact" 
                      icon={<CreditCardOutlined />} 
                      description="Handles billing and payments"
                    />
                  </Steps>

                  {/* Tenant Admin */}
                  <Card size="small" title="Tenant Administrator" style={{ background: '#fafafa' }}>
                    <Descriptions column={2} bordered size="small">
                      <Descriptions.Item label="Name">
                        {organization.tenantAdmin?.firstName} {organization.tenantAdmin?.middleName} {organization.tenantAdmin?.lastName}
                      </Descriptions.Item>
                      <Descriptions.Item label="Email">
                        <Space>
                          <MailOutlined />
                          {organization.tenantAdmin?.email}
                          {organization.tenantAdmin?.emailVarified ? (
                            <Tag color="green">Verified</Tag>
                          ) : (
                            <Tag color="orange">Not Verified</Tag>
                          )}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Phone">
                        <Space>
                          <PhoneOutlined />
                          {organization.tenantAdmin?.phone || 'N/A'}
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Address">
                        <Space>
                          <EnvironmentOutlined />
                          {organization.tenantAdmin?.address || 'N/A'}
                        </Space>
                      </Descriptions.Item>
                                       </Descriptions>
                  </Card>

                  {/* Contract Admin */}
                  {organization.contractAdmin ? (
                    <Card size="small" title="Contract Administrator" style={{ background: '#fafafa' }}>
                      <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Name">
                          {organization.contractAdmin?.firstName} {organization.contractAdmin?.middleName} {organization.contractAdmin?.lastName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Email">
                          <Space>
                            <MailOutlined />
                            {organization.contractAdmin?.email}
                            {organization.contractAdmin?.emailVarified ? (
                              <Tag color="green">Verified</Tag>
                            ) : (
                              <Tag color="orange">Not Verified</Tag>
                            )}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Phone">
                          <Space>
                            <PhoneOutlined />
                            {organization.contractAdmin?.phone || 'N/A'}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Address">
                          <Space>
                            <EnvironmentOutlined />
                            {organization.contractAdmin?.address || 'N/A'}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Tenant ID">
                          {organization.contractAdmin?.tenantId || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="User ID">
                          {organization.contractAdmin?.id || 'N/A'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  ) : (
                    <Alert
                      message="No Contract Administrator"
                      description="This organization does not have a contract administrator assigned."
                      type="info"
                      showIcon
                    />
                  )}

                  {/* Billing Contact */}
                  {organization.billingContact ? (
                    <Card size="small" title="Billing Contact" style={{ background: '#fafafa' }}>
                      <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="Name">
                          {organization.billingContact?.firstName} {organization.billingContact?.middleName} {organization.billingContact?.lastName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Email">
                          <Space>
                            <MailOutlined />
                            {organization.billingContact?.email}
                            {organization.billingContact?.emailVarified ? (
                              <Tag color="green">Verified</Tag>
                            ) : (
                              <Tag color="orange">Not Verified</Tag>
                            )}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Phone">
                          <Space>
                            <PhoneOutlined />
                            {organization.billingContact?.phone || 'N/A'}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Address">
                          <Space>
                            <EnvironmentOutlined />
                            {organization.billingContact?.address || 'N/A'}
                          </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Tenant ID">
                          {organization.billingContact?.tenantId || 'N/A'}
                        </Descriptions.Item>
                        <Descriptions.Item label="User ID">
                          {organization.billingContact?.id || 'N/A'}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  ) : (
                    <Alert
                      message="No Billing Contact"
                      description="This organization does not have a billing contact assigned."
                      type="info"
                      showIcon
                    />
                  )}
                </Space>
              </TabPane>
              
           
            </Tabs>
          </Card>
        </Col>
        
        <Col xs={24} lg={6}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Status Card */}
            <Card size="small" title="Organization Status" style={{ background: '#fafafa' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Approval Status</Text>
                  <Tag color={organization.status === 0 ? 'orange' : 'green'}>
                    {organization.status === 0 ? 'PENDING' : 'APPROVED'}
                  </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Active Status</Text>
                  <Tag color={organization.isActive ? 'green' : 'red'}>
                    {organization.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </Tag>
                </div>
                {organization.status === 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text>Approved Date</Text>
                    <Text>
                      {organization.approvedAt ? new Date(organization.approvedAt).toLocaleDateString() : 'N/A'}
                    </Text>
                  </div>
                )}
              </Space>
            </Card>

            {/* Quick Actions */}
        
<Card size="small" title="Quick Actions" style={{ background: '#fafafa' }}>
  <Space direction="vertical" style={{ width: '100%' }} size="small">
    <Button 
      type="primary" 
      block
      icon={<CheckCircleOutlined />}
      loading={approvingOrgId === organization.id}
      onClick={() => handleApproveOrganization(organization.id)}
      disabled={organization.status !== 0}  // Disable if not pending
    >
      {organization.status === 0 ? "Approve Organization" : "Organization Approved"}
    </Button>
  </Space>
</Card>

                    </Space>
        </Col>
      </Row>
    </div>
  );
};

export default OrganizationDetailPage;